import React from "react";
import {
  Participant as ParticipantType,
  RemoteParticipant,
  Room as RoomType,
  TwilioError,
  VideoTrack,
} from "twilio-video";
import styled from "styled-components";
import { useRoom } from "../hooks/useRoom";
import { useRemoteParticipant } from "../hooks/useRemoteParticipant";
import { getExistingVideoTrack } from "../components/helpers/getExistingTrack";
import { getTrack } from "../utils/getTrack";
import ActionRow from "../components/ActionRow/ActionRow";
import Chat from "../components/Chat/Chat";
import { useChat } from "../hooks/useChat";
import { SendLinkModalProvider } from "../context/SendLinkModalContext";
import FocusedParticipant from "../components/FocusedParticipant/FocusedParticipant";
import FacialRecognitionHandler from "../components/Handlers/FacialRecognitionHandler/FacialRecognitionHandler";
import ParticipantList from "../components/Participant/ParticipantList";
import EffectsModal from "../components/Modals/EffectsModal";
import { showToast } from "../utils/showToast";
import { WaitingList } from "../components/WaitingPatientsList/WaitingPatientsList";
import { useInfo } from "../hooks/useInfo";
import { useFullpageLoading } from "../hooks/useFullPageLoading";

const RoomContainer = styled("div")`
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const RoomContent = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
`;

const ParticipantsWrapper = styled("div")`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  padding: 6px;
  box-sizing: border-box;
`;

const LoadingOverlay = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 20;
  color: #fff;
`;

const Spinner = styled("div")`
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top: 6px solid #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Message = styled("p")`
  margin-top: 20px;
  text-align: center;
  font-size: 1rem;
  line-height: 1.5;
`;

const RetryButton = styled("button")`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;


interface IRoomProps {
  room: RoomType;
  token?: string;
  roomName?: string;
}

const Room = ({ room, token, roomName }: IRoomProps) => {
  const { handleChangeFocused, currentFocused, handleRecording, handleLeaveRoom } = useRoom();
  const { setRemoteParticipant } = useRemoteParticipant();
  const { loadMessages } = useChat();
  const { handleShowFullPageLoading } = useFullpageLoading();

  const [reconnectionMessage, setReconnectionMessage] = React.useState("");
  const [isReconnecting, setIsReconnecting] = React.useState(false);
  const [manualReconnect, setManualReconnect] = React.useState(false);
  const [isOffline, setIsOffline] = React.useState(false);
  const [showWaitingList, setShowWaitingList] = React.useState(false);
  const toggleWaitingList = () => setShowWaitingList(prev => !prev);

  const { telemedicineData } = useInfo();

  const getCameraTrack = (participantFocused: ParticipantType) => {
    return getTrack("camera", getExistingVideoTrack(participantFocused)) as VideoTrack;
  };

  React.useEffect(() => {
    const handleNetworkQuality = (qualityLevel: number) => {
      if (qualityLevel < 2) {
        setReconnectionMessage("Conexão instável. Tentando manter a chamada ativa...");
        setIsReconnecting(true);
      } else {
        setReconnectionMessage("");
        setIsReconnecting(false);
      }
    };

    const participantConnected = (participant: RemoteParticipant) => {
      if (!room.participants.has(participant.sid)) {
        const existingTrack = getCameraTrack(participant);
        if (existingTrack) {
          handleChangeFocused("remote", existingTrack);
        } else {
          participant.once("trackSubscribed", (track) => {
            if (track.kind === "video" && track.name !== "screen") {
              handleChangeFocused("remote", track as VideoTrack);
            }
          });
        }
      }
      setRemoteParticipant(participant);
    };

    // Quando a pessoa reconecta, a conexão nova pode chegar antes da saída da antiga: mantém quem ainda está na sala
    const participantDisconnected = () => {
      const remaining = Array.from(room.participants.values());
      if (remaining.length > 0) {
        setRemoteParticipant(remaining[remaining.length - 1]);
        return;
      }
      handleChangeFocused("local", getCameraTrack(room.localParticipant));
      setRemoteParticipant(undefined);
    };

    // Sem error é a saída pelo botão. Com error (outra conexão com a mesma identidade, queda de rede),
    // volta para a tela de entrada em vez de deixar a sala sem vídeo na tela
    const roomDisconnected = (_: RoomType, error?: TwilioError) => {
      if (!error) return;
      const duplicateIdentity = error.code === 53205;
      showToast("error", {
        text: duplicateIdentity
          ? "Você entrou nesta sala em outra aba ou dispositivo."
          : "A conexão com a sala foi perdida. Entre novamente.",
        timer: 10000,
      });
      handleLeaveRoom(duplicateIdentity);
    };

    const participantReconnecting = () => {
      setReconnectionMessage(
        "Notamos uma queda em sua conexão. Tentando reconectar automaticamente."
      );
      setIsReconnecting(true);

      setTimeout(() => {
        if (!isReconnecting) return;

        setManualReconnect(true);
        setIsReconnecting(false);
      }, 10000);
    };

    const participantReconnected = () => {
      setIsReconnecting(false);
      setManualReconnect(false);
    };

    room.localParticipant.on("networkQualityLevelChanged", handleNetworkQuality);
    room.on("participantConnected", participantConnected);
    room.on("participantDisconnected", participantDisconnected);
    room.on("disconnected", roomDisconnected);
    room.on("reconnecting", participantReconnecting);
    room.on("reconnected", participantReconnected);
    room.on("recordingStarted", () => handleRecording(true));
    room.on("recordingStopped", () => handleRecording(false));

    room.participants.forEach(participantConnected);

    if (currentFocused === "") {
      const remote = Array.from(room.participants.values()).find(
        (p) => p.identity !== room.localParticipant.identity
      );
    
      const localTrack = getCameraTrack(room.localParticipant);
    
      if (remote) {
        handleChangeFocused("local", localTrack);
        setRemoteParticipant(remote);
      } else {
        handleChangeFocused("local", localTrack);
      }
    }

    loadMessages(false);

    return () => {
      room.removeAllListeners();
      room.off("participantConnected", participantConnected);
    };
  }, [room, currentFocused, handleChangeFocused, handleRecording, handleLeaveRoom, loadMessages, token, isReconnecting]);

  const checkInternetConnection = async () => {
    try {
      const response = await fetch("https://app.amplimed.com.br", {
        method: "HEAD",
        mode: "no-cors",
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleReconnect = async () => {
    handleShowFullPageLoading(true);

    const isConnected = await checkInternetConnection();

    if (isConnected) {
      handleLeaveRoom();
    } else {
      showToast("error", {
        text: "Sem conexão no momento. Verifique sua internet e tente novamente.",
        timer: 10000,
      });
    }

    handleShowFullPageLoading(false);
  };

  React.useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
    };

    const handleOnline = () => {
      setIsOffline(false);
    };

    // Adicionar eventos para monitorar a perda e restauração da conexão
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    // Limpeza dos eventos quando o componente for desmontado
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [manualReconnect]);

  return (
    <RoomContainer>
      {(isReconnecting || manualReconnect || isOffline) && (
        <LoadingOverlay>
          {isOffline ? (
            <Message>Falha na conexão. Aguardando rede para reconexão...</Message>
          ) : isReconnecting ? (
            <>
              <Spinner />
              <Message>{reconnectionMessage}</Message>
            </>
          ) : manualReconnect ? (
            <>
              <Message>
                Não foi possível reconectar automaticamente. Clique no botão abaixo para tentar reconectar manualmente.
              </Message>
              <RetryButton style={{ backgroundColor: "#955fe1" }} onClick={handleReconnect}>
                Reconectar
              </RetryButton>
            </>
          ) : null}
        </LoadingOverlay>
      )}
      <RoomContent>
        <ParticipantsWrapper>
          <FocusedParticipant room={room} />
          <ParticipantList room={room} />
        </ParticipantsWrapper>
        <Chat />
        {showWaitingList && <WaitingList onToggleWaitingList={toggleWaitingList} />}
      </RoomContent>
      <SendLinkModalProvider>
        <ActionRow room={room} onToggleWaitingList={toggleWaitingList} />
      </SendLinkModalProvider>
      <FacialRecognitionHandler room={room} />
      <EffectsModal />
    </RoomContainer>
  );
};

export default Room;
